import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  AppBar, Box, Button, Container, IconButton, MenuItem, Stack, TextField, Toolbar,
  Tooltip, Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, useLocation } from "react-router-dom";

import { api } from "../api/client";
import type { Organization, Paginated } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { useOrganization, useOrganizationSync } from "../lib/useOrganization";

export function AppShell() {
  const { me, logout, can } = useAuth();
  const { organization, setOrganization } = useOrganization();
  useOrganizationSync();
  const lokalizacja = useLocation();

  const { data: podmioty } = useQuery({
    queryKey: ["organizations"],
    queryFn: () => api.get<Paginated<Organization>>("/organizations"),
  });

  const lista = podmioty?.results ?? [];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "grey.50" }}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar variant="dense" sx={{ gap: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mr: 2 }}>
            Kosztorysy
          </Typography>

          <Button component={Link} to="/projekty"
                  color={lokalizacja.pathname.startsWith("/projekty") ? "primary" : "inherit"}>
            Projekty
          </Button>
          {can("contractor.view") && (
            <Button component={Link} to="/wykonawcy"
                    color={lokalizacja.pathname.startsWith("/wykonawcy")
                      ? "primary" : "inherit"}>
              Wykonawcy
            </Button>
          )}
          {can("report.view_portfolio") && (
            <Button component={Link} to="/portfel"
                    color={lokalizacja.pathname.startsWith("/portfel") ? "primary" : "inherit"}>
              Portfel
            </Button>
          )}

          {/* Szablony to konfiguracja systemu, nie ekran produkcji: stoją
              obok projektów, bo administrator nie ma do nich dostępu (§6.5). */}
          {can("admin.manage_config") && (
            <Button component={Link} to="/szablony"
                    color={lokalizacja.pathname.startsWith("/szablony")
                      ? "primary" : "inherit"}>
              Szablony
            </Button>
          )}

          <Box sx={{ flex: 1 }} />

          {/* Przełącznik pokazujemy dopiero, gdy jest co przełączać —
              przy jednej spółce byłby tylko szumem. */}
          {lista.length > 1 && (
            <TextField
              select value={organization ?? ""} sx={{ minWidth: 200 }}
              onChange={(e) => setOrganization(e.target.value || null)}
              label="Podmiot"
            >
              <MenuItem value="">Wszystkie podmioty</MenuItem>
              {lista.map((o) => (
                <MenuItem key={o.id} value={o.code}>{o.name}</MenuItem>
              ))}
            </TextField>
          )}

          <Stack direction="row" alignItems="center" spacing={1}>
            {/* Zwykły odsyłacz, nie ``Link`` routera: instrukcja jest osobnym
                dokumentem serwowanym przez nginx, a nie ekranem aplikacji.
                Otwiera się obok, bo czyta się ją **w trakcie** pracy. */}
            <Tooltip title="Instrukcja obsługi">
              <IconButton component="a" href="/pomoc" target="_blank"
                          rel="noopener" color="inherit" aria-label="Instrukcja obsługi">
                <HelpOutlineIcon />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" color="text.secondary">
              {me?.first_name || me?.username}
            </Typography>
            <Button onClick={logout} startIcon={<LogoutIcon />} color="inherit">
              Wyloguj
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      <Container maxWidth={false} sx={{ py: 3 }}>
        <Outlet />
      </Container>
    </Box>
  );
}
